<template>
    <div class="popular-games">
        <div class="section-header">
            <h2 class="section-title">Popular Slots</h2>
        </div>
        
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="loading-spinner">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                    </path>
                </svg>
                <p>Loading popular slots...</p>
            </div>
        </div>

        <!-- Games Grid: 7 Hacksaw games from SlotsGateway (all playable) -->
        <div v-else-if="displayGames.length > 0" class="games-grid">
            <div class="game-card" v-for="(game, index) in displayGames" :key="game.code"
                 @click="launchGame(game)">
                <div class="game-image-wrapper game-top-section">
                    <img v-if="game.banner" :src="game.banner" :alt="game.name" class="game-image" @error="setDefaultImage" />
                    <img v-else-if="game.image" :src="game.image" :alt="game.name" class="game-image" @error="setDefaultImage" />
                    <div v-else class="default-slot-image" :class="getGameBgClass(index)">
                        <span class="slot-game-name">{{ game.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
            <p>No slots available at the moment</p>
        </div>
        
        <!-- See All Slots Link at Bottom -->
        <div v-if="!loading && displayGames.length > 0" class="see-all-section">
            <router-link to="/slots" class="see-all-link">
                <span class="see-all-text">See All Slots</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </router-link>
        </div>

        <!-- Game Modal -->
        <transition name="game-modal-fade">
            <div v-if="shouldShowModal" class="game-modal-overlay" @click="closeGame" role="dialog" aria-modal="true" :aria-label="`Playing ${currentGameName}. Click outside or press Escape to close.`">
                <div class="game-modal" @click.stop role="document">
                    <!-- <div class="game-modal-header">
                        <h3>{{ currentGameName }}</h3>
                        <button type="button" class="close-btn" @click="closeGame" aria-label="Close game">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="close-btn-text">Close</span>
                        </button>
                    </div> -->
                    <div class="game-modal-content">
                        <iframe 
                            :src="gameSession.launch_url" 
                            frameborder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                    </div>
                    <button type="button" class="game-modal-close-floating" @click.stop="closeGame" aria-label="Close game">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';

export default {
    name: 'PopularGames',
    data() {
        return {
            loading: false,
            allGames: [],
            gameSession: null,
            currentGameName: ''
        }
    },
    computed: {
        ...mapGetters(['authenticated']),
        shouldShowModal() {
            // Only show modal if we have a game session AND we're on the home page
            // Don't show on slots, casino, or other game pages
            const restrictedRoutes = ['/slots', '/our-casino', '/casino', '/live-casino'];
            const currentPath = this.$route.path;
            const isRestrictedRoute = restrictedRoutes.some(route => currentPath.includes(route));
            
            return this.gameSession && 
                   this.gameSession.launch_url && 
                   !isRestrictedRoute;
        },
        displayGames() {
            // 7 Hacksaw games from SlotsGateway
            return this.allGames.slice(0, 7);
        }
    },
    watch: {
        '$route': {
            handler(newRoute) {
                // Close game modal when route changes OR on restricted routes
                const restrictedRoutes = ['/slots', '/our-casino', '/casino', '/live-casino'];
                const isRestrictedRoute = restrictedRoutes.some(route => newRoute.path.includes(route));
                
                if (this.gameSession && isRestrictedRoute) {
                    this.closeGame();
                }
            },
            immediate: true
        }
    },
    created() {
        // Ensure clean state on component creation
        this.gameSession = null;
        this.currentGameName = '';
        document.body.classList.remove('slot-game-playing');
        
        // Force close if on restricted route
        const restrictedRoutes = ['/slots', '/our-casino', '/casino', '/live-casino'];
        const isRestrictedRoute = restrictedRoutes.some(route => this.$route.path.includes(route));
        if (isRestrictedRoute && this.gameSession) {
            this.closeGame();
        }
    },
    mounted() {
        this.fetchPopularGames();
        document.addEventListener('keydown', this.handleModalKeydown);
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleModalKeydown);
        if (this.gameSession) {
            this.closeGame();
        }
    },
    methods: {
        ...mapActions(['notificationShow']),
        ...mapActions('slots', ['launchGameSeamless']),
        
        async fetchPopularGames() {
            this.loading = true;
            try {
                const res = await axios.get('/slotsgateway/games', {
                    params: { currency: 'USD', provider: 'hacksaw', limit: 7 }
                });
                if (res.data.success && Array.isArray(res.data.games)) {
                    this.allGames = res.data.games.map(g => this.normalizeSlotsGatewayGame(g));
                } else {
                    this.allGames = [];
                }
            } catch (error) {
                console.error('Error fetching popular games (SlotsGateway Hacksaw):', error);
                this.allGames = [];
            } finally {
                this.loading = false;
            }
        },

        normalizeSlotsGatewayGame(g) {
            const id = g.id_hash || g.id || g.game_id || g.code;
            const name = g.name || g.game_name || g.title || '';
            const image = g.image || g.banner || g.thumb || g.small_image || g.icon || '';
            return {
                code: id,
                name,
                provider_code: 'SLOTSGATEWAY',
                image,
                banner: image,
                status: g.status != null ? g.status : 1
            };
        },

        handleModalKeydown(e) {
            if (e.key === 'Escape' && this.shouldShowModal) {
                this.closeGame();
            }
        },
        async launchGame(game) {
            if (!this.authenticated) {
                this.notificationShow({ type: 'error', message: 'Please sign in to play slots.' });
                this.$root.$emit('open-signin-modal-app');
                return;
            }

            try {
                this.currentGameName = game.name;
                
                // Launch the game using the Vuex store action
                const session = await this.launchGameSeamless({
                    providerCode: 'SLOTSGATEWAY',
                    gameCode: game.code
                });

                if (session) {
                    this.gameSession = session;
                    document.body.classList.add('slot-game-playing');
                } else {
                    this.notificationShow({ 
                        type: 'error', 
                        message: 'Failed to launch game. Please try again.' 
                    });
                }
            } catch (error) {
                console.error('Error launching game:', error);
                this.notificationShow({ 
                    type: 'error', 
                    message: error.message || 'Failed to launch game. Please try again.' 
                });
            }
        },

        closeGame() {
            // Clear game session data
            this.gameSession = null;
            this.currentGameName = '';
            
            // Remove body class
            document.body.classList.remove('slot-game-playing');
            
            // Force remove any lingering iframes
            this.$nextTick(() => {
                const iframes = document.querySelectorAll('.game-modal-content iframe');
                iframes.forEach(iframe => {
                    if (iframe.parentNode) {
                        iframe.src = 'about:blank';
                        iframe.parentNode.removeChild(iframe);
                    }
                });
            });
        },

        getProviderName(providerCode) {
            if (!providerCode) return '';
            const code = providerCode.toUpperCase();
            const providers = {
                'DREAMTECH': 'Dreamtech'
            };
            return providers[code] || code.replace(/_/g, ' ');
        },

        getGameBgClass(index) {
            const bgClasses = ['casino-bg', 'card2-bg', 'card4-bg', 'card5-bg', 'card3-bg', 'card6-bg', 'casino-bg', 'card2-bg', 'card4-bg', 'card5-bg', 'card3-bg', 'card6-bg'];
            return bgClasses[index % bgClasses.length];
        },

        setDefaultImage(event) {
            event.target.style.display = 'none';
            const defaultImage = event.target.parentElement.querySelector('.default-image');
            if (defaultImage) {
                defaultImage.style.display = 'flex';
            }
        }
    }
}
</script>

<style scoped>
.popular-games {
    padding: var(--spacing-xl) 0 var(--spacing-xl) 0;
    margin: 0 21px 0 21px;
}

@media only screen and (max-width: 768px) {
    .popular-games {
        padding: var(--spacing-md);
        margin: 0;
    }
}

.section-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0 var(--spacing-xl) 0;
    padding: var(--spacing-lg) 0;
}

.section-title {
    font-size: 32px;
    font-weight: 900;
    text-align: center;
    margin: 0;
    background: var(--accent-main-txt);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 2px;
    position: relative;
    display: inline-block;
}

.see-all-section {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: var(--spacing-xl);
    padding-top: var(--spacing-lg);
}

.see-all-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
}

.see-all-link:hover {
    color: var(--accent-btn-purple);
    transform: translateX(2px);
}

.see-all-link svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    fill: none;
    transition: transform 0.3s ease;
}

.see-all-link:hover svg {
    transform: translateX(2px);
}


/* Loading State */
.loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.loading-spinner svg {
    color: var(--accent-yellow);
}

.loading-spinner p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 16px;
}

/* Games Grid */
.games-grid {
    display: flex;
    gap: 28px;
    max-width: 1400px;
    margin: 0 auto;
    flex-wrap: wrap;
    justify-content: center;
}

.game-card {
    border-radius: 12px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    width: 166px;
    height: 222px;
    display: flex;
    flex-direction: column;
    background: #1a1a2e;
    border: 2px solid #2d2d44;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.game-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
    border-color: #3d3d54;
}

.game-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
    border-color: #3d3d54;
}

/* Bottom 5 cards: cloud/frost effect, not clickable */
.game-card.game-card-locked {
    pointer-events: none;
    cursor: default;
}

.game-card.game-card-locked::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.08) 0%,
        rgba(255, 255, 255, 0.18) 40%,
        rgba(240, 248, 255, 0.22) 100%
    );
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-radius: 12px;
    z-index: 2;
    pointer-events: none;
}

.game-card.game-card-locked:hover {
    transform: none;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    border-color: #2d2d44;
}

/* Top section: image area (~70-75% of card) */
.game-image-wrapper.game-top-section {
    width: 100%;
    height: 100%;
    margin: 0;
    border-radius: 12px 12px 0 0;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: radial-gradient(circle at 20% 10%, rgba(59, 130, 246, 0.28), rgba(12, 16, 29, 1) 65%);
    flex-shrink: 0;
}

.game-image {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
    background: transparent;
}

.default-slot-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.slot-game-name {
    font-size: 18px;
    font-weight: 900;
    color: #ffffff;
    text-transform: uppercase;
    text-align: center;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6);
    letter-spacing: 1px;
    line-height: 1.3;
}

/* Title section below image */
.game-title-section {
    padding: 14px 12px;
    text-align: center;
    background: #1a1a2e;
    min-height: 65px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    gap: 6px;
}

.game-title-section .game-title {
    font-size: 15px;
    font-weight: 700;
    color: #ffffff;
    text-transform: uppercase;
    margin: 0;
    letter-spacing: 0.5px;
    line-height: 1.4;
    text-align: center;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

.game-title-section .game-provider {
    font-size: 11px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.65);
    text-transform: uppercase;
    margin: 0;
    letter-spacing: 0.5px;
    line-height: 1.3;
    text-align: center;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

/* Background styles for slots without images */
.default-slot-image.casino-bg {
    background: linear-gradient(135deg, #1e8449 0%, #27ae60 50%, #2ecc71 100%);
}

.default-slot-image.card2-bg {
    background: linear-gradient(135deg, #7d3c98 0%, #9b59b6 50%, #af7ac5 100%);
}

.default-slot-image.card3-bg {
    background: linear-gradient(135deg, #922b21 0%, #c0392b 50%, #e74c3c 100%);
}

.default-slot-image.card4-bg {
    background: linear-gradient(135deg, #1a5276 0%, #2980b9 50%, #3498db 100%);
}

.default-slot-image.card5-bg {
    background: linear-gradient(135deg, #b7950b 0%, #f1c40f 50%, #f4d03f 100%);
}

.default-slot-image.card6-bg {
    background: linear-gradient(135deg, #d35400 0%, #e67e22 50%, #f39c12 100%);
}


/* Game Modal */
.game-modal-fade-enter-active,
.game-modal-fade-leave-active {
    transition: opacity 0.25s ease;
}
.game-modal-fade-enter-active .game-modal,
.game-modal-fade-leave-active .game-modal {
    transition: transform 0.25s ease;
}
.game-modal-fade-enter-from,
.game-modal-fade-leave-to {
    opacity: 0;
}
.game-modal-fade-enter-from .game-modal,
.game-modal-fade-leave-to .game-modal {
    transform: scale(0.98);
}

/* Slot game modal: full screen */
.game-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: #000;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    z-index: 9999999 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
}

.game-modal {
    background: #000;
    border: none;
    border-radius: 0;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    z-index: 10000000 !important;
}

.game-modal .close-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    min-width: 44px;
    min-height: 44px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    transition: all 0.2s ease;
}
.game-modal .close-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
}
.game-modal .close-btn:focus-visible {
    outline: 2px solid var(--color-copper);
    outline-offset: 2px;
}
.game-modal .close-btn-text {
    display: none;
}
@media (min-width: 480px) {
    .game-modal .close-btn-text { display: inline; }
}

.game-modal-close-floating {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    width: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    background: rgba(0, 0, 0, 0.6) !important;
    border: 2px solid rgba(255, 255, 255, 0.25) !important;
    border-radius: 50% !important;
    color: #fff !important;
    cursor: pointer !important;
    z-index: 10000003 !important;
    transition: all 0.2s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}
.game-modal-close-floating:hover {
    background: rgba(0, 0, 0, 0.85) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
    transform: scale(1.05);
}

.game-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 72px 12px 24px !important;
    border-bottom: 1px solid rgba(212, 165, 116, 0.2);
    position: relative;
    z-index: 10000001 !important;
    flex-shrink: 0;
}

.game-modal-header h3 {
    font-size: 20px;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}


.game-modal-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: #000;
    position: relative;
}

.game-modal-content iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    display: block;
}

/* Better z-index management */
body.slot-game-playing {
    overflow: hidden !important;
}

@media (max-width: 1200px) {
    .games-grid {
        justify-content: center;
        gap: 20px;
    }
}

@media (max-width: 1024px) {
    .game-modal-overlay {
        padding: 0 !important;
    }
}

@media (max-width: 768px) {
    .games-grid {
        gap: 16px;
        justify-content: center;
    }
    
    .game-card {
        width: 180px;
        height: 220px;
    }
    
    .game-image-wrapper.game-top-section {
        height: 155px;
    }
    
    .section-title {
        font-size: 22px;
    }
    
    .game-title-section .game-title {
        font-size: 12px;
        line-height: 1.3;
    }
    
    .game-title-section .game-provider {
        font-size: 9px;
        line-height: 1.2;
    }
    
    .game-title-section {
        padding: 10px 10px;
        min-height: 55px;
        gap: 5px;
    }
    
    .slot-game-name {
        font-size: 15px;
    }

    .game-modal-overlay {
        padding: 0 !important;
    }

    .game-modal-header {
        padding: 10px 72px 10px 16px !important;
    }

    .game-modal-header h3 {
        font-size: 16px;
    }
}

@media only screen and (max-width: 480px) {
    .game-card {
        width: 160px;
        height: 200px;
    }
    
    .game-image-wrapper.game-top-section {
        height: 135px;
    }
    
    .game-title-section .game-title {
        font-size: 11px;
        line-height: 1.3;
    }
    
    .game-title-section .game-provider {
        font-size: 8px;
        line-height: 1.2;
    }
    
    .game-title-section {
        padding: 8px 8px;
        min-height: 50px;
        gap: 4px;
    }
    
    .slot-game-name {
        font-size: 13px;
    }

    .game-modal-overlay {
        padding: 0 !important;
    }

    .game-modal-header {
        padding: 8px 72px 8px 12px !important;
    }

    .game-modal-header h3 {
        font-size: 14px;
    }
}
</style>
